import React, { useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import MultiSelectModal from "./MultiSelectModal";
import SelectProfessional from "./SelectProfessional";
import TimeSlotSelector from "./Timeslotselector";
import bookingApi from "../../api/booking/BookingApi";
import toast from "react-hot-toast";
import ProcessingBar from "../../components/ProcessingBar/ProcessingBar";
import sericesApi from "../../api/service/service.api";

const AddAppointmentModal = ({
  isOpen,
  closeModal,
  slotInfo,
  staffs,
  refreshData,
}) => {
  const user = useSelector((state) => state.user);

  const operatorId = user?.user?._id;

  const [selectedNailServices, setSelectedNailServices] = useState([]);
  const [selectedBeautyServices, setSelectedBeautyServices] = useState([]);
  const [note, setNote] = useState("");

  const [totalTimeNail, setTotalTimeNail] = useState(0);
  const [totalTimeBeauty, setTotalTimeBeauty] = useState(0);

  const [selectedProfessional, setSelectedProfessional] = useState([]);

  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [nails, setNails] = useState([]);
  const [beauty, setBeauty] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [customerName, setCustomerName] = useState(user?.user?.name || "");
  const [email, setEmail] = useState(user?.user?.email || "");
  const [mobile, setMobile] = useState(user?.user?.phone || "");

  // Initialize time and staff from slotInfo
  useEffect(() => {
    if (slotInfo) {
      const formattedTime = moment(slotInfo.start).format("YYYY-MM-DD hh:mm A");
      setSelectedDateTime(formattedTime);
      setSelectedProfessional(slotInfo.resourceId || "");

      // ✅ Clear previous selections
      setSelectedNailServices([]);
      setSelectedBeautyServices([]);
      setTotalTimeNail(0);
      setTotalTimeBeauty(0);

      setCustomerName(user?.user?.name || "");
      setEmail(user?.user?.email || "");
      setMobile(user?.user?.phone || "");
      setNote("");
    }
  }, [slotInfo]);

  useEffect(() => {
    if (slotInfo && staffs.length > 0) {
      const formattedTime = moment(slotInfo.start).format("YYYY-MM-DD hh:mm A");
      setSelectedDateTime(formattedTime);

      // 🧠 Find matching staff object from staff list
      const matchingStaff = staffs.find(
        (staff) => staff._id === slotInfo.resourceId
      );

      if (matchingStaff) {
        setSelectedProfessional(matchingStaff); // ✅ set by ID
      }
    }
  }, [slotInfo, staffs]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const [nailRes, beautyRes] = await Promise.all([
          sericesApi.getallNail({ operatorId }),
          sericesApi.getallBeauty({ operatorId }),
        ]);
        setNails(nailRes?.response?.data?.nailservices || []);
        setBeauty(beautyRes?.response?.data?.beautyservices || []);
      } catch (err) {
        toast.error("Failed to fetch services");
      }
    };
    fetchServices();
  }, [operatorId]);

  // console.log({ nails });

  // console.log({ beauty });

  const handleSubmit = async () => {
    if (
      selectedNailServices.length === 0 &&
      selectedBeautyServices.length === 0
    ) {
      toast.error("Please select at least one Nail or Beauty service.");
      return;
    }
    try {
      const formData = {
        operator: operatorId,
        user: user?.user?._id,
        selectedNailServices,
        selectedBeautyServices,
        selectedProfessionalId: selectedProfessional,
        selectedDateTime,
        totalTimeNail,
        totalTimeBeauty,
        name: customerName,
        email,
        mobile,
        message: note,
        bookingtype: "walk-in",
      };

      setIsProcessing(true);
      await bookingApi.createBooking(formData);
      toast.success("Appointment created!");
      closeModal();
      refreshData(); // Refresh bookings
    } catch (err) {
      console.error(err);
      toast.error("Failed to create booking");
    } finally {
      setIsProcessing(false);
    }
  };

  // 🔸 Get matching service IDs from selected staff
  const availableNailIds = selectedProfessional?.nailService || [];
  const availableBeautyIds = selectedProfessional?.beautyService || [];

  // 🔸 Filter full service list by IDs
  const filteredNails = nails.filter((nail) =>
    availableNailIds.includes(nail._id)
  );
  const filteredBeauty = beauty.filter((b) =>
    availableBeautyIds.includes(b._id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 ">
      <div className=" bg-dry text-white rounded-xl w-full max-w-2xl p-6 space-y-6 shadow-xl border border-yellowlight relative overflow-y-auto max-h-[90vh]">
        <h2 className="text-lg font-semibold text-yellowlight">
          NEW WALK IN APPOINTMENT
        </h2>

        <div className="text-sm">
          <p>
            <strong>Appointment Time:</strong>{" "}
            {selectedDateTime ? (
              <span className="text-yellowlight">{selectedDateTime}</span>
            ) : (
              <span className="text-gray-400">Not selected</span>
            )}
          </p>
        </div>

        {filteredNails.length !== 0 && (
          <MultiSelectModal
            options={filteredNails}
            totalTime={totalTimeNail}
            setTotalTime={setTotalTimeNail}
            selectedValues={selectedNailServices}
            setSelectedValues={setSelectedNailServices}
            label="Nail Services"
          />
        )}

        {filteredBeauty.length !== 0 && (
          <MultiSelectModal
            options={filteredBeauty}
            totalTime={totalTimeBeauty}
            setTotalTime={setTotalTimeBeauty}
            selectedValues={selectedBeautyServices}
            setSelectedValues={setSelectedBeautyServices}
            label="Beauty Services"
          />
        )}

        <SelectProfessional
          label="Staff"
          staffs={staffs}
          selectedId={selectedProfessional}
          onChange={setSelectedProfessional}
          selectedNailservices={selectedNailServices}
          selectedBeautyservices={selectedBeautyServices}
        />

        <TimeSlotSelector
          selectedDateTime={selectedDateTime}
          setSelectedDateTime={setSelectedDateTime}
          staffs={staffs}
          selectedProfessionalId={selectedProfessional}
        />

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Customer Name</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full mt-1 p-3 rounded-lg bg-dry border border-yellowlight text-sm text-white"
              placeholder="Enter customer name"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 p-3 rounded-lg bg-dry border border-yellowlight text-sm text-white"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Mobile</label>
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className="w-full mt-1 p-3 rounded-lg bg-dry border border-yellowlight text-sm text-white"
              placeholder="Enter phone number"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Customer’s Message</label>
          <textarea
            className="w-full mt-1 p-3 rounded-lg bg-dry border border-yellowlight text-sm"
            rows="3"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write any notes here..."
          />
        </div>

        <div className="flex justify-between pt-4">
          <button
            onClick={closeModal}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              !selectedProfessional ||
              !selectedDateTime ||
              (selectedNailServices.length === 0 &&
                selectedBeautyServices.length === 0)
            }
            className={`px-6 py-2 rounded-lg text-sm font-semibold transition ${
              !selectedProfessional ||
              !selectedDateTime ||
              (selectedNailServices.length === 0 &&
                selectedBeautyServices.length === 0)
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-yellowlight text-black hover:bg-yellowdark"
            }`}
          >
            {isProcessing ? <ProcessingBar /> : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAppointmentModal;
